import { ScrollAnimContext } from "@/contexts/ScrollAnimContext";
import ShowcaseItem from "./ShowcaseItem";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { cubicBezier, motion, useMotionValueEvent, useTransform } from "motion/react";
import { type Camera, type WorldNode, drawLines, renderLine, render, TEAM_COLOURS } from "@/libs/20thCenturyFox";
import { CameraContext } from "@/contexts/CameraContext";

/**
 * Obtain a random team assignment
 * @returns Either "user" or "dev"
 */
const randomTeam = () => {
  return Math.random() < 0.5 ? "user" : "dev";
};

/**
 * Define the position of our nodes
 * In context of 1920x1080 world space (scaled up and down by the camera based on the viewport size)
 */
const worldNodes: WorldNode[] = [
  {
    id: "a",
    kind: "showcase",
    position: { x: -300, y: -300 },
    team: randomTeam(),
  },
  {
    id: "b",
    kind: "showcase",
    position: { x: 0, y: 300 },
    team: randomTeam(),
  },
  {
    id: "c",
    kind: "showcase",
    position: { x: 300, y: -300 },
    team: randomTeam(),
  },
  // Some invisible spawned nodes to connect up the showcase nodes during the canvas scene
  {
    id: "d",
    kind: "spawned",
    position: { x: -100, y: 100 },
    team: randomTeam(),
  },
  {
    id: "d",
    kind: "spawned",
    position: { x: 100, y: -100 },
    team: randomTeam(),
  },
];

export default function ExperiencesShowcase() {
  // -- Animations -- //
  const scrollYProgress = useContext(ScrollAnimContext);

  // Animations for showcase section
  const { showcaseStart, showcaseEnd } = { showcaseStart: 0.04, showcaseEnd: 0.13 };
  const showcaseProgress = useTransform(scrollYProgress, [showcaseStart, showcaseEnd], [0, 1]); // Control animation for showcase nodes
  const transitionOutProgress = useTransform(scrollYProgress, [showcaseEnd + 0.06, showcaseEnd + 0.12], [0, 1], {
    ease: cubicBezier(0.55, 0, 1, 0.45),
  }); // For shrinking and transforming the showcase nodes out

  const zoom = useTransform(scrollYProgress, [showcaseEnd + 0.14, 0.87], [1, 0.25], {
    ease: cubicBezier(0.18, 0, 0.63, 1),
  }); // Zoom out for canvas section

  // Animations for extended canvas section
  const systemsProgress = useTransform(scrollYProgress, [0.27, 1], [0, 1]);
  const showcaseOpacity = useTransform(systemsProgress, [0, 0.06], [1, 0]); // To crossfade showcase nodes and canvas
  const canvasOpacity = useTransform(systemsProgress, [0, 0.06, 0.4, 0.68, 0.75], [0, 0.5, 0.8, 0.8, 0], { ease: cubicBezier(0.55, 0, 1, 0.45) }); // Fade in and out canvas

  // Line colours
  const userLineColour = useTransform(systemsProgress, [0.18, 0.3], ["rgb(153, 153, 153)", TEAM_COLOURS.USER]);
  const devLineColour = useTransform(systemsProgress, [0.39, 0.51], ["rgb(153, 153, 153)", TEAM_COLOURS.DEV]);

  // States
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [cameraZoom, setCameraZoom] = useState(1);

  // -- Functions -- //
  /**
   * Propogate zoom value changes to camera
   */
  useMotionValueEvent(zoom, "change", (latest) => {
    setCameraZoom(latest);
  });

  /**
   * Set active showcase item based on scroll progress
   */
  useMotionValueEvent(showcaseProgress, "change", (latest) => {
    // Define deadzones and disable showcase active state within them
    const deadZones: [number, number][] = [
      [0, 0.08],
      [0.92, 1],
    ];

    for (const [start, end] of deadZones) {
      if (latest >= start && latest <= end) {
        setActiveIndex(null);
        return;
      }
    }

    // Calculate the amount of progress per node and determine the currently active index
    const showcaseNodes = worldNodes.filter((node) => node.kind === "showcase");

    const progressPer = 0.8 / showcaseNodes.length;
    const index = Math.min(Math.floor((latest - 0.08) / progressPer), showcaseNodes.length - 1);

    // Set active index
    setActiveIndex(index);
  });

  // -- Camera setup -- //
  const [viewportSize, setViewportSize] = useState({ x: window.innerWidth, y: window.innerHeight });

  // Update viewport size on resize
  const handleResize = () => {
    setViewportSize({ x: window.innerWidth, y: window.innerHeight });
  };

  useEffect(() => {
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Initialise camera
  const camera: Camera = {
    zoom: cameraZoom,
    offset: {
      x: viewportSize.x / 2,
      y: viewportSize.y / 2,
    },
    viewportSize,
  };

  // -- Rendering -- //
  // Spawn in extended nodes
  const extendedNodes: WorldNode[] = useMemo(() => {
    const nodes: WorldNode[] = [];
    const canvasWidth = camera.viewportSize.x * 5;
    const canvasHeight = camera.viewportSize.y * 5;
    const count = 450;

    for (let i = 0; i < count; i++) {
      // Spread nodes randomly within the canvas area, avoiding a grid
      const x = (Math.random() - 0.5) * canvasWidth;
      const y = (Math.random() - 0.5) * canvasHeight;

      nodes.push({
        id: `ext-${i}`,
        kind: "spawned",
        team: randomTeam(), // Assign random team
        position: { x, y },
      });
    }
    return nodes;
  }, []);

  // Render nodes and lines
  const allNodes = useMemo(() => [...worldNodes, ...extendedNodes], [worldNodes, extendedNodes]);
  const lines = useMemo(() => drawLines(allNodes), [allNodes]);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  /**
   * Draw lines and nodes onto the canvas
   */
  const drawCanvas = () => {
    // Obtain a reference to the canvas context
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Clear the entire canvas & set styles
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = "#6b7280"; // default line colour
    ctx.fillStyle = "rgb(153, 153, 153)"; // node colour
    ctx.lineWidth = 2;

    // Draw lines
    // We only draw the lines connecting between the showcase nodes if systemsProgress has yet to start
    for (const line of lines) {
      // Check if we should skip this line
      // if ((line.start.id.startsWith("ext-") || line.end.id.startsWith("ext-")) && scrollYProgress.get() < 0.73) {
      //   continue;
      // }

      // Check if the line is being drawn between a user, dev, or mixed team nodes and set colour accordingly
      const startNode = allNodes.find((node) => node.id === line.start.id);
      const endNode = allNodes.find((node) => node.id === line.end.id);
      if (startNode && endNode) {
        if (startNode.team === "user" && endNode.team === "user") {
          ctx.strokeStyle = userLineColour.get();
        } else if (startNode.team === "dev" && endNode.team === "dev") {
          ctx.strokeStyle = devLineColour.get();
        } else {
          ctx.strokeStyle = "#6b7280"; // Mixed team line colour
        }
      }

      // Draw line
      const { start, end } = renderLine(line, camera);
      ctx.beginPath();
      ctx.moveTo(start.x, start.y);
      ctx.lineTo(end.x, end.y);
      ctx.stroke();
    }

    // Draw nodes
    for (const node of allNodes) {
      // Project world position to screen
      const { x, y, height, width } = render(node, camera, { height: camera.viewportSize.y / 100, width: camera.viewportSize.x / 100 });

      ctx.beginPath();
      ctx.arc(x, y, Math.max(Number(width), Number(height)) / 2, 0, Math.PI * 2);
      // ctx.fillStyle = node.team === "user" ? userNodeColour.get() : devNodeColour.get(); // Colour based on team
      ctx.fill();
    }
  };

  // Redraw canvas on changes
  useEffect(() => {
    drawCanvas();
  }, [lines, cameraZoom, allNodes, camera]);

  useEffect(() => {
    // Redraw on screen size change
    window.addEventListener("resize", () => drawCanvas);
    return () => window.removeEventListener("resize", drawCanvas);
  }, []);

  return (
    <div className='relative h-full w-full'>
      {/* For drawing lines */}
      <motion.canvas
        ref={canvasRef}
        width={camera.viewportSize.x}
        height={camera.viewportSize.y}
        className='absolute inset-0 z-0'
        style={{
          opacity: canvasOpacity,
        }}
      />

      {/* DOM nodes rendered from world space */}
      <ScrollAnimContext.Provider value={transitionOutProgress}>
        <CameraContext.Provider value={camera}>
          <motion.div
            className='relative z-10 h-full w-full'
            style={{
              // Hide the showcase nodes towards the end as the canvas takes over
              opacity: showcaseOpacity,
            }}
          >
            {allNodes.map((node, index) => {
              if (node.kind !== "showcase") return null; // Only render showcase nodes as DOM elements
              return (
                <ShowcaseItem key={node.id} node={node} active={activeIndex === index} className={"transition-opacity duration-500 ease-in-out"} />
              );
            })}
          </motion.div>
        </CameraContext.Provider>
      </ScrollAnimContext.Provider>
    </div>
  );
}
